import React, { useCallback, useEffect, useRef } from 'react';
import useCropSeason from 'hooks/useCropSeason';
import { Property } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import {
  CropZone,
  RecordSourceType
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import {
  AREA_TYPE_BOUNDARY,
  AREA_TYPE_REPORTED
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/areaTypeConstants';
import { getCropZonesFromProperties } from 'helpers/propertyHelpers';
import { Maths } from 'helpers/maths';
import usePopulateMapData from 'hooks/usePopulateMapData';
import { FETCH_TYPES } from 'helpers/constants';
import { OperationPropertyVO } from './useOperationProperties';

const PERCENTAGE_BASE = 100;

interface UseMapCropZonesReturn {
  fetchCropZoneSeasons: (
    cropSeasonIds: string[]
  ) => Promise<Map<string, Array<{ id: string }>>>;
  buildCropZoneMap: (properties: Property[]) => Map<string, Property>;
  mapCropZones: (
    cropZoneMapProp: Map<string, Property>,
    cropZoneSeasonsMap: Map<string, Array<{ id: string }>>,
    propertiesData: OperationPropertyVO[],
    sourceType?: RecordSourceType
  ) => CropZone[];
  propertyLandingPageData: any;
  fieldsAndAreasGeoJSONCollection: any;
  reloadData: (params?: { seasonIds?: string[] }) => void | Promise<unknown>;
  isPropertiesMapLoading: boolean;
}

/**
 * Hook for mapping crop zones with their associated data.
 * Provides functions to fetch crop seasons, build crop zone maps, and map properties to crop zones.
 */
const useMapCropZones = (): UseMapCropZonesReturn => {
  const { getCropSeason } = useCropSeason();

  // Fetch crop zone data to map recommendation crop zones to full crop zone objects
  const selectedCropSeasonIds = React.useMemo(() => {
    return JSON.parse(localStorage.getItem('selectedCropSeasons') ?? '[]');
  }, []);

  const {
    dataSorted: propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    reloadData,
    loading: isPropertiesMapLoading
  } = usePopulateMapData({
    fetchType: FETCH_TYPES.propertiesMap,
    cropSeasonsId: selectedCropSeasonIds
  });

  // Keep a ref to fieldsAndAreasGeoJSONCollection so mapCropZones can read
  // the latest value at call time WITHOUT adding it to its dependency array.
  // This prevents mapCropZones from getting a new identity when GeoJSON loads,
  // which would cascade through useOperationFormById and re-trigger data fetches.
  const fieldsAndAreasRef = useRef(fieldsAndAreasGeoJSONCollection);
  fieldsAndAreasRef.current = fieldsAndAreasGeoJSONCollection;

  useEffect(() => {
    reloadData({ seasonIds: selectedCropSeasonIds });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCropSeasonIds]);

  // Function to fetch crop seasons and build the crop zone to season IDs mapping
  const fetchCropZoneSeasons = useCallback(
    async (
      cropSeasonIds: string[]
    ): Promise<Map<string, Array<{ id: string }>>> => {
      if (!cropSeasonIds?.length) return new Map();

      const seasonPromises = cropSeasonIds.map(seasonId =>
        getCropSeason(seasonId)
      );
      const seasons = await Promise.all(seasonPromises);

      const cropZoneSeasonsMap = new Map<string, Array<{ id: string }>>();

      const cropZonesWithSeasonId: Array<{
        cropzoneId: string;
        seasonId: string;
      }> = seasons
        .filter((season: any) => season?.cropZones)
        .flatMap((season: any) =>
          season.cropZones.map((cz: any) => ({
            cropzoneId: cz.cropzoneId,
            seasonId: season.id
          }))
        );

      cropZonesWithSeasonId.forEach(({ cropzoneId, seasonId }) => {
        const existing = cropZoneSeasonsMap.get(cropzoneId) || [];
        if (!existing.some(s => s.id === seasonId)) {
          existing.push({ id: seasonId });
        }
        cropZoneSeasonsMap.set(cropzoneId, existing);
      });

      return cropZoneSeasonsMap;
    },
    [getCropSeason]
  );

  // Build a map from properties, keyed by cropZoneId
  const buildCropZoneMap = useCallback((properties: Property[]) => {
    return properties.reduce((acc: Map<string, Property>, prop: Property) => {
      if (!acc.has(prop.cropZoneId)) {
        acc.set(prop.cropZoneId, prop);
      }
      return acc;
    }, new Map<string, Property>());
  }, []);

  const getAllCropZones = useCallback(
    (propertiesData: OperationPropertyVO[]) => {
      const farmIds = new Set(propertiesData.map(prop => prop.farmId));
      const filteredProperties = propertyLandingPageData?.properties?.filter(
        (prop: any) => farmIds.has(prop.id)
      );
      return getCropZonesFromProperties(filteredProperties) ?? [];
    },
    [propertyLandingPageData]
  );

  const getSelectedAreaType = useCallback(
    (
      cropZone: Property | undefined,
      reportedArea: number,
      boundaryArea: number,
      sourceType: RecordSourceType | undefined,
      coveragePercent: number
    ) => {
      if (
        sourceType === RecordSourceType.RECOMMENDATION ||
        sourceType === RecordSourceType.PLANS
      ) {
        if (reportedArea === 0) return AREA_TYPE_BOUNDARY;

        if (!cropZone || coveragePercent <= 0) {
          return AREA_TYPE_BOUNDARY;
        }

        const percentage = Maths.divideSafeRound(coveragePercent, 100);
        const totalArea = Maths.divideSafeRound(cropZone.areaValue, percentage);

        const boundaryDiff = Math.abs(boundaryArea - totalArea);
        const reportedDiff = Math.abs(reportedArea - totalArea);

        if (boundaryDiff < reportedDiff) {
          return AREA_TYPE_BOUNDARY;
        }
        return AREA_TYPE_REPORTED;
      }

      return cropZone?.areaType === 'BOUNDARY'
        ? AREA_TYPE_BOUNDARY
        : AREA_TYPE_REPORTED;
    },
    []
  );

  const calculateCoveragePercent = useCallback(
    (
      appliedArea: number,
      totalArea: number,
      reportedArea: number,
      boundaryArea: number
    ) => {
      let coveragePercent = 0;
      if (appliedArea && totalArea > 0) {
        const denominator =
          reportedArea === 0 && boundaryArea > 0 ? boundaryArea : reportedArea;
        if (denominator > 0) {
          const ratio = Maths.divideSafeRound(appliedArea, denominator);
          coveragePercent = Maths.multiplyRound(ratio, 100);
        }
      }
      return coveragePercent;
    },
    []
  );

  // Map properties data to crop zones format
  const mapCropZones = useCallback(
    (
      cropZoneMapProp: Map<string, Property>,
      cropZoneSeasonsMap: Map<string, Array<{ id: string }>>,
      propertiesData: OperationPropertyVO[],
      sourceType?: RecordSourceType
    ): CropZone[] => {
      const allCropZones = getAllCropZones(propertiesData);

      return propertiesData.map(prop => {
        const cropZone = cropZoneMapProp.get(prop.cropZoneId);

        const foundCropZone = allCropZones.find(
          (cz: any) => cz.id === prop.cropZoneId
        );

        // Get crop seasons for this crop zone from the map
        const cropSeasons = cropZoneSeasonsMap.get(prop.cropZoneId) || [];

        // Calculate coverage percent using the same logic as recommendations
        // If reportedArea is 0, use boundaryArea; otherwise use reportedArea
        const totalArea = prop.cropZoneArea || 0;
        const reportedArea = foundCropZone?.reportedArea || 0;
        const boundaryArea = foundCropZone?.boundaryArea || 0;

        const coveragePercent = calculateCoveragePercent(
          prop.appliedArea,
          totalArea,
          reportedArea,
          boundaryArea
        );

        const selectedAreaType = getSelectedAreaType(
          cropZone,
          reportedArea,
          boundaryArea,
          sourceType,
          coveragePercent
        );

        const appliedAcreage =
          sourceType &&
          [RecordSourceType.RECOMMENDATION, RecordSourceType.PLANS].includes(
            sourceType
          )
            ? Maths.multiplyRound(
                totalArea,
                Maths.divide(prop.appliedAreaPercent, PERCENTAGE_BASE)
              )
            : prop.appliedArea;

        // Enrich with field GeoJSON features for weather centroid calculation.
        // Uses the ref to avoid adding fieldsAndAreasGeoJSONCollection as a
        // dependency — same approach as SelectPropertiesDrawer.getCropzoneFeatures.
        const fieldGeometry =
          fieldsAndAreasRef.current?.features?.filter(
            (feature: any) => prop.fieldId === feature.properties?.id
          ) || [];

        return {
          id: prop.cropZoneId,
          name: prop.cropZoneName,
          fieldId: prop.fieldId,
          fieldName: prop.fieldName,
          isFieldArchived: prop.fieldArchived,
          crop: {
            name: prop.cropName,
            id: prop.cropId
          },
          cropSeasons,
          geometry: prop.cropZoneGeometry || undefined,
          fieldGeometry,
          propertyId: prop.farmId,
          propertyName: prop.farmName,
          isPropertyArchived: prop.farmArchived,
          area: prop.cropZoneArea || 0,
          appliedAcreage,
          coveragePercent: prop.appliedAreaPercent ?? coveragePercent,
          reportedArea,
          boundaryArea,
          selectedAreaType
        };
      });
    },
    [getAllCropZones, calculateCoveragePercent, getSelectedAreaType]
  );

  return {
    fetchCropZoneSeasons,
    buildCropZoneMap,
    mapCropZones,
    propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    reloadData,
    isPropertiesMapLoading
  };
};

export default useMapCropZones;
